import React, { useEffect, useRef, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { useIsAllowedPermission } from '@mint-lib/profile-helpers';
import styled, { css } from '@mint-lib/styled';

import { Box, Button, Divider, IconButton, Input, Popover } from '@myn/mui';

import { BUILDER_ROW_TYPES } from '../../../../../../constants/campaignBuilder.js';
import { TRACKING_EVENTS } from '../../../../../../types/performanceTracking.js';
import { BuilderTableColumn, BuilderTableRow } from '../../../../types.js';
import FormattedField from '../FormattedField/FormattedField.jsx';
import TableEditCell from '../TableEditCell/TableEditCell.jsx';
import { ZERO_TIME } from './constants.js';
import TimeItem from './TimeItem.jsx';
import { Time } from './types.js';
import { parseTimeValue } from './utils.js';

type Props = {
  value: string | null;
  column: BuilderTableColumn;
  row: BuilderTableRow;
  cellColor: string;
};

const TimeField: React.FC<Props> = ({ column, row, value, cellColor }) => {
  const { t } = useTranslation('@myn/planning');

  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const isAllowed = useIsAllowedPermission('campaignPlanningFeature:modify');
  const {
    id: columnId,
    startMeasurePerformance,
    isTimeRange,
    onlyTileEditable,
  } = column;
  const {
    id: rowId,
    type,
    isBuyingPlanLocked,
    isCampaignCurrencyMode,
    isPredictivePlanningMode,
    isArchivedVersion,
    scale,
  } = row.original;
  const isTotalRow = type === BUILDER_ROW_TYPES.TOTAL;
  const isDisabled = type !== BUILDER_ROW_TYPES.TILE && !!onlyTileEditable;
  const isReadOnlyField =
    !isAllowed ||
    isPredictivePlanningMode ||
    isArchivedVersion ||
    isCampaignCurrencyMode ||
    isBuyingPlanLocked;
  const displayTimeValue =
    columnId === 'time' ? value?.split(':').slice(0, -1).join(':') : value;
  const isClearBtnShown = !!displayTimeValue && !isFocused;

  const initialStartTime = parseTimeValue('startTime', value);
  const initialEndTime = parseTimeValue('endTime', value);

  const [localStartTime, setLocalStartTime] = useState(initialStartTime);
  const [localEndTime, setLocalEndTime] = useState(initialEndTime);

  useEffect(() => {
    if (isFocused && inputWrapperRef.current) {
      setAnchorEl(inputWrapperRef.current);
    }
  }, [isFocused]);

  const handlePopoverOpen = () => {
    setIsFocused(true);
  };

  const handlePopoverClose = () => {
    handleCancel();
    setIsFocused(false);
    setAnchorEl(null);
  };

  const handleChangeStartTime = (newValue: Time) => {
    setLocalStartTime(newValue);
  };
  const handleChangeEndTime = (newValue: Time) => {
    setLocalEndTime(newValue);
  };

  const handleSave = () => {
    const newTime = isTimeRange
      ? `${localStartTime.hours || ZERO_TIME}:${
          localStartTime.minutes || ZERO_TIME
        }-${localEndTime.hours || ZERO_TIME}:${
          localEndTime.minutes || ZERO_TIME
        }`
      : `${localStartTime.hours || ZERO_TIME}:${
          localStartTime.minutes || ZERO_TIME
        }`;

    const isTimeEqual = value === `${newTime}:${ZERO_TIME}`;
    const isTimeRangeEqual = value === newTime;

    if ((!isTimeRange && !isTimeEqual) || (isTimeRange && !isTimeRangeEqual)) {
      startMeasurePerformance(TRACKING_EVENTS.TABLE_UPDATE);
      column.updateRow(
        {
          id: rowId,
        },
        columnId,
        newTime,
      );
    }

    setIsFocused(false);
    setAnchorEl(null);
  };

  const handleCancel = () => {
    setLocalStartTime(initialStartTime);
    setLocalEndTime(initialEndTime);
  };

  const handleClearSelect = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    if (isAllowed && !isPredictivePlanningMode) {
      startMeasurePerformance(TRACKING_EVENTS.TABLE_UPDATE);
      column.updateRow(
        {
          id: rowId,
        },
        columnId,
        null,
      );
    }

    setIsFocused(false);
  };

  if (isTotalRow || isDisabled) {
    return null;
  }

  if (isReadOnlyField) {
    return (
      <FormattedField
        scale={scale}
        value={displayTimeValue ?? null}
        color={cellColor}
        variant="text"
      />
    );
  }

  if (!isFocused) {
    return (
      <TableEditCell
        scale={scale}
        isDeleteButtonShown={isClearBtnShown}
        deleteValueFunc={handleClearSelect}
        showEditModeFunc={handlePopoverOpen}
        startMeasurePerformance={startMeasurePerformance}
        value={displayTimeValue ?? null}
        variant="text"
      />
    );
  }

  return (
    <Wrapper>
      <InputWrapper ref={inputWrapperRef}>
        <StyledTextInput
          type="text"
          value={displayTimeValue ?? ''}
          onChange={() => null}
          placeholder={t('Select...')}
          onClick={handlePopoverOpen}
          endAdornment={
            <IconButton
              variant="icon"
              icon="ChevronDown"
              onClick={handlePopoverOpen}
              sx={{
                zoom: scale,
              }}
            />
          }
          $scale={scale}
        />
      </InputWrapper>

      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        onClose={handlePopoverClose}
      >
        <PopoverContent>
          {isTimeRange ? (
            <MultipleItemContainer>
              <TimeItem
                title={t('Starting Time')}
                time={localStartTime}
                onChangeTime={handleChangeStartTime}
              />
              <Divider orientation="vertical" sx={{ height: '96px' }} />
              <TimeItem
                title={t('Ending Time')}
                time={localEndTime}
                onChangeTime={handleChangeEndTime}
              />
            </MultipleItemContainer>
          ) : (
            <SingleItemContainer>
              <TimeItem
                time={localStartTime}
                onChangeTime={handleChangeStartTime}
              />
            </SingleItemContainer>
          )}
          <ControlButtons isTimeRange={isTimeRange}>
            <Button variant="text" onClick={handlePopoverClose}>
              {t('Cancel')}
            </Button>
            <Button variant="contained" onClick={handleSave}>
              {t('Confirm')}
            </Button>
          </ControlButtons>
        </PopoverContent>
      </Popover>
    </Wrapper>
  );
};

export default TimeField;

const Wrapper = styled(Box)`
  width: 100%;
  height: 100%;
`;

const InputWrapper = styled(Box)`
  width: 100%;
  height: 100%;
  & > div {
    height: 100%;
  }
`;

const StyledTextInput = styled(Input)<{
  $scale: number;
}>`
  box-shadow: none;
  min-height: 0;
  width: 100%;
  height: 100%;
  background: transparent;

  input {
    user-select: none;
    cursor: default;
    caret-color: transparent;
  }

  box-shadow: ${({ theme }) =>
    `inset 0 0 0 1px ${theme.palette.action.focusBorder}`};

  ${({ $scale }) => css`
    font-size: ${0.875 * $scale}rem;
    line-height: ${1.25 * $scale};
  `}
`;

const PopoverContent = styled(Box)`
  min-width: 200px;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(1.75)}`};
`;
const SingleItemContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing(1)};

  & > div {
    padding-top: 0;
  }
`;
const MultipleItemContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  column-gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;
const ControlButtons = styled(Box)<{
  isTimeRange?: boolean;
}>`
  display: flex;
  justify-content: ${({ isTimeRange }) =>
    isTimeRange ? 'flex-end' : 'space-between'};
  gap: ${({ theme }) => theme.spacing(1)};
  align-self: flex-end;
`;
